import { useState } from "react";
import { createPortal } from "react-dom";
import { X, Loader2 } from "lucide-react";

const RATING_OPTIONS: { value: number; emoji: string; labelEn: string; labelAr: string }[] = [
  { value: 1, emoji: "😡", labelEn: "Very unsatisfied", labelAr: "غير راضٍ جداً" },
  { value: 2, emoji: "😕", labelEn: "Unsatisfied", labelAr: "غير راضٍ" },
  { value: 3, emoji: "😐", labelEn: "Neutral", labelAr: "محايد" },
  { value: 4, emoji: "🙂", labelEn: "Satisfied", labelAr: "راضٍ" },
  { value: 5, emoji: "😍", labelEn: "Very satisfied", labelAr: "راضٍ جداً" },
];

export interface FeedbackModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (rating: number, comment?: string) => Promise<void>;
  systemLanguage?: string;
}

export function FeedbackModal({ open, onClose, onSubmit, systemLanguage = "en" }: FeedbackModalProps) {
  const isRTL = systemLanguage === "ar";

  const t = {
    title: isRTL ? "شارك تجربتك" : "Share Your Feedback",
    description: isRTL ? "كيف كانت تجربتك مع المساعد؟" : "How was your experience with the assistant?",
    commentPlaceholder: isRTL ? "أضف تعليقاً (اختياري)..." : "Add a comment (optional)...",
    submit: isRTL ? "إرسال" : "Submit",
    cancel: isRTL ? "إلغاء" : "Cancel",
    ratingRequired: isRTL ? "الرجاء اختيار تقييم" : "Please select a rating",
    error: isRTL ? "حدث خطأ. يرجى المحاولة مرة أخرى." : "Something went wrong. Please try again.",
  };

  const [selectedRating, setSelectedRating] = useState<number | null>(null);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const reset = () => {
    setSelectedRating(null);
    setComment("");
    setError(null);
  };

  const handleSubmit = async () => {
    if (!selectedRating) {
      setError(t.ratingRequired);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await onSubmit(selectedRating, comment.trim() ? comment : undefined);
      reset();
      onClose();
    } catch {
      setError(t.error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const activeLabel =
    selectedRating !== null
      ? (isRTL ? RATING_OPTIONS[selectedRating - 1].labelAr : RATING_OPTIONS[selectedRating - 1].labelEn)
      : "";

  return createPortal(
    <div
      dir={isRTL ? "rtl" : "ltr"}
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 backdrop-blur-sm"
      style={{ position: "fixed", inset: 0, zIndex: 99999, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "rgba(0,0,0,0.4)" }}
    >
      <div
        style={{
          position: "relative", width: "100%", maxWidth: "448px", borderRadius: "16px",
          backgroundColor: "white", boxShadow: "0 20px 60px rgba(0,0,0,0.2)", padding: "24px",
          margin: "0 16px",
        }}
      >
        <button
          onClick={handleClose}
          style={{
            position: "absolute", top: "16px",
            [isRTL ? "left" : "right"]: "16px",
            background: "none", border: "none", cursor: "pointer",
            color: "#9ca3af", padding: "4px",
          }}
          aria-label={t.cancel}
        >
          <X size={18} />
        </button>

        <h2 style={{ fontSize: "18px", fontWeight: 600, color: "#111827", marginBottom: "4px", marginTop: 0 }}>
          {t.title}
        </h2>
        <p style={{ fontSize: "12px", color: "#6b7280", marginBottom: "20px" }}>
          {t.description}
        </p>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px", marginBottom: "8px", padding: "0 4px" }}>
          {RATING_OPTIONS.map((option) => {
            const isSelected = selectedRating === option.value;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  setSelectedRating(option.value);
                  setError(null);
                }}
                style={{
                  display: "flex", flexDirection: "column", alignItems: "center", gap: "4px",
                  padding: "8px", borderRadius: "12px", border: "none", cursor: "pointer",
                  background: isSelected ? "rgba(51,51,255,0.1)" : "transparent",
                  outline: isSelected ? "2px solid #33f" : "none",
                  transform: isSelected ? "scale(1.1)" : "scale(1)",
                  opacity: isSelected ? 1 : 0.6,
                  transition: "all 0.15s ease",
                }}
                aria-label={isRTL ? option.labelAr : option.labelEn}
              >
                <span style={{ fontSize: "30px", lineHeight: 1 }}>{option.emoji}</span>
                <span style={{ fontSize: "10px", color: "#6b7280" }}>{option.value}</span>
              </button>
            );
          })}
        </div>

        <div style={{ textAlign: "center", fontSize: "12px", color: "#4b5563", minHeight: "16px", marginBottom: "16px" }}>
          {activeLabel}
        </div>

        <div style={{ marginBottom: "16px" }}>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t.commentPlaceholder}
            rows={3}
            style={{
              width: "100%", padding: "12px", border: "1px solid #e5e7eb", borderRadius: "8px",
              fontSize: "14px", resize: "none", outline: "none", boxSizing: "border-box",
              fontFamily: "inherit",
            }}
            onFocus={(e) => { e.target.style.borderColor = "#33f"; }}
            onBlur={(e) => { e.target.style.borderColor = "#e5e7eb"; }}
          />
        </div>

        {error && (
          <p style={{ fontSize: "14px", color: "#ef4444", marginBottom: "16px" }}>{error}</p>
        )}

        <div style={{ display: "flex", gap: "12px" }}>
          <button
            onClick={handleClose}
            style={{
              flex: 1, padding: "8px 16px", borderRadius: "8px", border: "1px solid #e5e7eb",
              color: "#4b5563", fontSize: "14px", fontWeight: 500, cursor: "pointer",
              background: "white", transition: "background 0.15s",
            }}
            onMouseEnter={(e) => { (e.target as HTMLElement).style.background = "#f9fafb"; }}
            onMouseLeave={(e) => { (e.target as HTMLElement).style.background = "white"; }}
          >
            {t.cancel}
          </button>
          <button
            onClick={handleSubmit}
            disabled={!selectedRating || loading}
            style={{
              flex: 1, padding: "8px 16px", borderRadius: "8px", border: "none",
              background: "#33f", color: "white", fontSize: "14px", fontWeight: 500,
              cursor: !selectedRating || loading ? "not-allowed" : "pointer",
              opacity: !selectedRating || loading ? 0.5 : 1,
              display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
              transition: "opacity 0.15s",
            }}
          >
            {loading && <Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} />}
            {t.submit}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
